import { CatalogTable } from '../../components/CatalogTable';
import { useAuth } from '../../lib/auth-context-value';
import { formatMoney } from '../../lib/format';
import { COLOR_PALETTE } from '../../lib/palette';
import {
  activityTypes as activityTypesApi,
  lists as listsApi,
  lossReasons as lossReasonsApi,
  products as productsApi,
  tags as tagsApi,
} from '../../lib/resources';

/** Bolinha de cor + nome — a primeira coluna de tags e tipos de atividade. */
function ColorName({ color, name }: { color: string | null; name: string }) {
  return (
    <span className="flex items-center gap-2 font-medium text-slate-800">
      <span
        className="h-3 w-3 shrink-0 rounded-full border border-slate-200"
        style={{ backgroundColor: color ?? 'transparent' }}
        aria-hidden
      />
      {name}
    </span>
  );
}

/** Escrever em cadastros auxiliares exige `aux_admin` (ou administrador). */
const useCanWriteAux = () => useAuth().can('aux_admin');

export function TagsSection() {
  return (
    <CatalogTable
      title="Tags"
      subtitle="Etiquetas aplicadas a leads e mostradas nos cards de negócio."
      noun={{ one: 'tag', many: 'tags' }}
      queryKey="tags"
      api={tagsApi}
      canWrite={useCanWriteAux()}
      labelOf={(tag) => tag.name}
      columns={[
        { header: 'Tag', render: (tag) => <ColorName color={tag.color} name={tag.name} /> },
        {
          header: 'Descrição',
          render: (tag) => <span className="text-slate-500">{tag.description ?? '—'}</span>,
        },
      ]}
      fields={[
        {
          key: 'name',
          label: 'Nome',
          kind: 'text',
          required: true,
          placeholder: 'Nome da tag',
        },
        {
          key: 'description',
          label: 'Descrição',
          kind: 'text',
          placeholder: 'Descrição da tag',
        },
        // A tag nasce com a 1ª cor pré-marcada.
        { key: 'color', label: 'Cor', kind: 'color', initial: COLOR_PALETTE[0] },
      ]}
    />
  );
}

export function ProductsSection() {
  return (
    <CatalogTable
      title="Produtos"
      subtitle="Catálogo usado em Negócio › Produtos e Valores. O preço é o sugerido."
      noun={{ one: 'produto', many: 'produtos' }}
      queryKey="products"
      api={productsApi}
      canWrite={useCanWriteAux()}
      labelOf={(product) => product.name}
      columns={[
        {
          header: 'Produto',
          render: (product) => (
            <span className="font-medium text-slate-800">{product.name}</span>
          ),
        },
        {
          header: 'Identificador (SKU)',
          render: (product) => <span className="text-slate-500">{product.id_sku ?? '—'}</span>,
        },
        { header: 'Preço', render: (product) => formatMoney(product.price) },
      ]}
      fields={[
        {
          key: 'name',
          label: 'Nome',
          kind: 'text',
          required: true,
          placeholder: 'Nome do produto',
        },
        {
          key: 'id_sku',
          label: 'Identificador (SKU)',
          kind: 'text',
          placeholder: 'produto123',
        },
        { key: 'price', label: 'Preço', kind: 'money', initial: 0 },
      ]}
    />
  );
}

export function LossReasonsSection() {
  return (
    <CatalogTable
      title="Motivos de perda"
      subtitle="Escolhidos ao perder um negócio."
      noun={{ one: 'motivo', many: 'motivos' }}
      queryKey="loss-reasons"
      api={lossReasonsApi}
      canWrite={useCanWriteAux()}
      labelOf={(reason) => reason.name}
      columns={[
        {
          header: 'Motivos de perda dos negócios',
          render: (reason) => <span className="font-medium text-slate-800">{reason.name}</span>,
        },
        {
          header: 'Obrigatório',
          render: (reason) => (
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                reason.requiredJustification
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              {reason.requiredJustification ? 'Sim' : 'Não'}
            </span>
          ),
        },
      ]}
      fields={[
        { key: 'name', label: 'Motivo', kind: 'text', required: true },
        {
          key: 'requiredJustification',
          label: 'Justificativa obrigatória',
          hint: 'Ao perder com este motivo, é preciso escrever uma justificativa.',
          kind: 'toggle',
          initial: false,
        },
      ]}
    />
  );
}

export function ListsSection() {
  return (
    <CatalogTable
      title="Listas"
      subtitle="Agrupamentos estáticos de leads. Os leads entram pela tela de Leads."
      noun={{ one: 'lista', many: 'listas' }}
      queryKey="lists"
      api={listsApi}
      canWrite={useCanWriteAux()}
      labelOf={(list) => list.name}
      columns={[
        {
          header: 'Listas',
          render: (list) => <span className="font-medium text-slate-800">{list.name}</span>,
        },
        {
          header: 'Descrição',
          render: (list) => <span className="text-slate-500">{list.description ?? '—'}</span>,
        },
      ]}
      fields={[
        {
          key: 'name',
          label: 'Nome',
          kind: 'text',
          required: true,
          placeholder: 'ex Lista 123',
        },
        { key: 'description', label: 'Descrição', kind: 'text' },
      ]}
    />
  );
}

export function ActivityTypesSection() {
  return (
    <CatalogTable
      title="Tipos de atividade"
      subtitle="Categorias das atividades; a cor aparece no calendário."
      noun={{ one: 'tipo de atividade', many: 'tipos de atividade' }}
      queryKey="activity-types"
      api={activityTypesApi}
      canWrite={useCanWriteAux()}
      labelOf={(type) => type.name}
      columns={[
        { header: 'Tipo', render: (type) => <ColorName color={type.color} name={type.name} /> },
      ]}
      fields={[
        { key: 'name', label: 'Tipo de atividade', kind: 'text', required: true },
        // Aqui nenhuma cor vem marcada.
        { key: 'color', label: 'Cor', kind: 'color', initial: null },
      ]}
    />
  );
}
